import React, { useState } from 'react'
import { computeSystemVerdict, computeTopActions } from '../lib/verdict'
import type { SystemVerdict, TopAction } from '../lib/verdict'
import type { Alert } from '../lib/api'

interface BucketHealth {
  projectId: string
  health: 'good' | 'warning' | 'critical'
}

interface Props {
  buckets: BucketHealth[]
  alerts: Alert[]
  totalAlerts: number
  criticalProjects: number
  totalErrors: number
}

const VERDICT_COLOR: Record<SystemVerdict['level'], string> = {
  unstable: 'var(--health-crit)',
  degraded:  'var(--health-warn)',
  stable:    'var(--health-good)',
}

const SEV_COLOR: Record<TopAction['severity'], string> = {
  critical: 'var(--health-crit)',
  high:     'var(--health-warn)',
}

const SEV_LABEL: Record<TopAction['severity'], string> = {
  critical: 'CRITIQUE',
  high:     'ÉLEVÉE',
}

/* ── Bloc source alerts (traçabilité vers la preuve) ────────────── */
function SourceList({ alerts, onClose }: { alerts: Alert[]; onClose: () => void }) {
  return (
    <div style={{
      position: 'absolute',
      top: 'calc(100% + 6px)',
      left: 0,
      zIndex: 100,
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      minWidth: 300,
      maxWidth: 420,
      padding: '8px 0',
    }}>
      <div style={{ padding: '6px 12px 4px', fontSize: 10, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
        Alertes sources ({alerts.length})
      </div>
      {alerts.map((a) => (
        <div key={a.id} style={{ padding: '6px 12px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--text)' }}>
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-dim)', fontSize: 10, marginRight: 6 }}>
            {a.at.slice(11, 19)}
          </span>
          <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>[{a.projectId}]</span>
          {a.message}
        </div>
      ))}
      <div style={{ padding: '4px 12px 0', textAlign: 'right' }}>
        <button onClick={onClose} style={{ fontSize: 10, color: 'var(--text-dim)', background: 'transparent', border: 'none', cursor: 'pointer' }}>
          Fermer
        </button>
      </div>
    </div>
  )
}

/* ── Carte action prioritaire ───────────────────────────────────── */
function ActionCard({ action }: { action: TopAction }) {
  const [showSources, setShowSources] = useState(false)
  const color = SEV_COLOR[action.severity]

  return (
    <div style={{
      position: 'relative',
      flex: '1 1 220px',
      minWidth: 0,
      background: 'var(--bg-2)',
      border: `1px solid ${color}44`,
      borderRadius: 'var(--radius-md)',
      padding: '10px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
        <span style={{
          fontSize: 10,
          fontWeight: 700,
          color,
          fontFamily: 'var(--font-mono)',
          background: `${color}1a`,
          padding: '1px 6px',
          borderRadius: '4px',
        }}>
          {SEV_LABEL[action.severity]}
        </span>
        {action.affectedCount > 1 && (
          <span style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
            {action.affectedCount} projets
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.4, marginBottom: '8px' }}>
        {action.message}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
        <span style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
          {action.projectId}
        </span>
        <button
          onClick={() => setShowSources((v) => !v)}
          style={{
            fontSize: 10,
            color: color,
            background: `${color}15`,
            border: `1px solid ${color}33`,
            borderRadius: '4px',
            padding: '1px 8px',
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
          }}
        >
          {showSources ? 'Masquer' : 'Voir détails →'}
        </button>
      </div>
      {showSources && (
        <SourceList alerts={action.sourceAlerts} onClose={() => setShowSources(false)} />
      )}
    </div>
  )
}

/* ── SystemBanner (principal) ───────────────────────────────────── */
export default function SystemBanner({ buckets, alerts, totalAlerts, criticalProjects, totalErrors }: Props) {
  const verdict = computeSystemVerdict(buckets, alerts)
  const topActions = computeTopActions(alerts, 3)
  const verdictColor = VERDICT_COLOR[verdict.level]
  const hasActions = topActions.length > 0

  return (
    <div style={{
      background: 'var(--surface)',
      border: `1px solid ${verdictColor}55`,
      borderLeft: `4px solid ${verdictColor}`,
      borderRadius: 'var(--radius-lg)',
      padding: '16px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      animationName: 'fadeIn',
      animationDuration: '0.3s',
    }}>

      {/* ── Ligne 1 : Verdict + KPIs ─────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>

        {/* Verdict */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '0 0 auto' }}>
          <span style={{
            width: 10, height: 10,
            borderRadius: '50%',
            background: verdictColor,
            boxShadow: `0 0 8px ${verdictColor}88`,
            flexShrink: 0,
            display: 'inline-block',
          }} />
          <span style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 800,
            fontFamily: 'var(--font-mono)',
            color: verdictColor,
            letterSpacing: '-0.02em',
          }}>
            ÉTAT SYSTÈME : {verdict.label}
          </span>
        </div>

        {/* Description */}
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', flex: '1 1 auto' }}>
          {verdict.description}
        </span>

        {/* KPIs compacts */}
        <div style={{ display: 'flex', gap: '20px', flex: '0 0 auto', flexWrap: 'wrap' }}>
          {[
            { val: totalAlerts,     label: 'Alertes élevées',    color: 'var(--health-warn)' },
            { val: totalErrors,     label: 'Erreurs détectées',  color: 'var(--health-crit)' },
            { val: criticalProjects,label: 'Projets critiques',  color: verdictColor },
          ].map(({ val, label, color }) => (
            <div key={label} style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 800, fontFamily: 'var(--font-mono)', color, lineHeight: 1.1 }}>
                {val}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 500 }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Ligne 2 : Actions prioritaires ───────────────────── */}
      {hasActions && (
        <div>
          <div style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--text-dim)',
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            marginBottom: '8px',
          }}>
            À TRAITER MAINTENANT
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {topActions.map((action, i) => (
              <ActionCard key={i} action={action} />
            ))}
          </div>
        </div>
      )}

      {/* ── Analyse IA (heuristique locale, source traçable) ── */}
      {verdict.sourceAlerts.length > 0 && (
        <div style={{
          fontSize: 11,
          color: 'var(--text-muted)',
          fontStyle: 'italic',
          borderTop: '1px solid var(--border)',
          paddingTop: '10px',
        }}>
          <span style={{ color: 'var(--text-dim)', fontWeight: 600, fontStyle: 'normal' }}>Analyse IA · </span>
          {buildAnalysisText(verdict, topActions)}
        </div>
      )}
    </div>
  )
}

/* Génère le texte d'analyse depuis les données réelles uniquement */
export function buildAnalysisText(verdict: SystemVerdict, actions: TopAction[]): string {
  if (verdict.level === 'stable') return 'Tous les systèmes fonctionnent normalement.'

  const top = actions[0]
  if (!top) return verdict.description

  const projectSuffix = top.affectedCount > 1
    ? `impactant ${top.affectedCount} projets`
    : `sur ${top.projectId}`

  return `Le problème principal vient de "${top.message}" ${projectSuffix}. Priorité ${top.severity === 'critical' ? 'maximale' : 'élevée'}.`
}
